import {type FC, useEffect, useState} from 'react';
import {getPlaylistItems, getPlaylists} from '../../apis/callYoutubeApi.ts';
import {
    ContentWrapper,
    DownloadContent,
    GoDownloadWrapper,
    MainContent,
    SlideContainer,
} from './types/Replay.styled.ts';
import type {PlaylistInfo} from './types';
import {FocusContext, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import PlayList from './component/PlayList.tsx';
import useReplayStore from '../../store/useReplayStore.ts';
import useGlobalStore from '../../store/useGlobalStore.ts';
import CustomPlayer from './component/CustomPlayer.tsx';
import ReplayPlayer from './component/ReplayPlayer.tsx';

const ReplayPage: FC = () => {
    const {showPlayer, playListMap, setPlayListMap} = useReplayStore();
    const [isSlided, setIsSlided] = useState(false);

    const fetchData = async () => {
        const res = await getPlaylists();
        if (res?.data?.items) {
            const playListData = res.data.items;
            const mkPlayListMap = new Map<string, Array<PlaylistInfo>>();
            const playlistsWithIndex = playListData.map((playlist: PlaylistInfo, index: number) => ({
                playlist,
                index,
            }));
            await Promise.all(
                playlistsWithIndex.map(async ({playlist, index}: { playlist: PlaylistInfo; index: number }) => {
                    try {
                        const data = await getPlaylistItems(playlist.id);
                        const items = data?.data?.items ?? [];
                        mkPlayListMap.set(`${index}::${playlist.snippet.title}`, items);
                    } catch {
                        // 404 등 에러 발생 시 해당 플레이리스트 제외
                    }
                })
            );
            setPlayListMap(mkPlayListMap);
        }
    };

    useEffect(() => {
        fetchData();
        setFocus('replay');
    }, []);

    const {ref: goDownloadPageRef, focused: goDownloadPageFocused} = useFocusable({
        focusKey: 'go-download-page',
        onEnterPress: () => {
            setIsSlided(true);
            setTimeout(() => {
                setFocus('player-control-close');
            }, 400);
        },
        onFocus: () => {
            if (goDownloadPageRef.current) {
                goDownloadPageRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        },
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus('replay');
            }
            if (direction === 'down') {
                setFocus('section-0');
            }
            setIsSlided(false);
            return false;
        },
    });

    const {
        ref: replayPageRef,
        focusKey: replayPageFocusKey,
        focusSelf: focusReplayPage,
    } = useFocusable({
        focusKey: 'replay-page',
        preferredChildFocusKey: 'go-download-page',
        saveLastFocusedChild: true,
        trackChildren: true,
        isFocusBoundary: true,
    });

    const {ref: replayPlayerRef, focusKey: replayPlayerFocusKey} = useFocusable({
        focusKey: 'replay-player',
        preferredChildFocusKey: 'player-control-close',
        saveLastFocusedChild: true,
        trackChildren: true,
        isFocusBoundary: true,
    });

    const {stack} = useGlobalStore();
    useEffect(() => {
        if (stack.length > 0) setFocus('go-download-page');
    }, [playListMap, focusReplayPage]);

    return (
        <>
            <SlideContainer $isSlided={isSlided}>
                <MainContent>
                    <FocusContext.Provider value={replayPageFocusKey}>
                        <GoDownloadWrapper ref={goDownloadPageRef} $focused={goDownloadPageFocused}>
                            다운로드 목록 보기
                        </GoDownloadWrapper>
                        <ContentWrapper ref={replayPageRef}>
                            {Array.from(playListMap.entries())
                                .sort(([keyA], [keyB]) => {
                                    const indexA = parseInt(keyA.split('::')[0]);
                                    const indexB = parseInt(keyB.split('::')[0]);
                                    return indexA - indexB;
                                })
                                .map(([videoKey, videoList], sectionIndex) => (
                                    <PlayList
                                        key={videoKey}
                                        title={videoKey.split('::')[1]}
                                        videoList={videoList}
                                        sectionIndex={sectionIndex}
                                    />
                                ))}
                        </ContentWrapper>
                    </FocusContext.Provider>
                </MainContent>

                <DownloadContent>
                    <CustomPlayer
                        onBack={() => {
                            setIsSlided(false);
                            setTimeout(() => {
                                setFocus('go-download-page');
                            }, 400);
                        }}
                    />
                </DownloadContent>
            </SlideContainer>

            <FocusContext.Provider value={replayPlayerFocusKey}>
                {showPlayer && (
                    <div ref={replayPlayerRef}>
                        <ReplayPlayer/>
                    </div>
                )}
            </FocusContext.Provider>
        </>
    );
};

export default ReplayPage;
